"use client";

import React from "react";
import { cn, getUniversalStyles } from "@/lib/utils";
import { getComponentByType } from "@/config/ecoBuilder/component-registry";
import { SiteProvider } from "@/components/ecoBuilder/store/SiteContext";

import { PageContext, type PageContextType } from "./PageContext";

interface Layer {
    id: string;
    type: string;
    name: string;
    props: Record<string, any>;
    children?: string[];
    parentId?: string;
}

interface ResolvedData {
    collections: Record<string, any>;
    menus: Record<string, any>;
}

// ═══════════════════════════════════════════════════════════════
// Helper: Extract Styles (Wrapper around shared utility)
// ═══════════════════════════════════════════════════════════════

const extractStyles = (props: any) => {
    const style = getUniversalStyles(props);

    // Filter out props that were consumed by getUniversalStyles to avoid passing them to DOM
    // Also filter out editor-specific props that should never be passed to DOM elements
    const {
        dimensions, width, height,
        padding, margin, gap,
        border, borderRadius, borderWidth, borderColor, borderStyle,
        display, flexDirection, justifyContent, alignItems, flexWrap, flex, alignSelf,
        backgroundColor, backgroundImage, backgroundSize, backgroundPosition,
        textAlign, color, fontSize, fontWeight, fontFamily, lineHeight, letterSpacing,
        position, top, left, right, bottom, zIndex,
        opacity, overflow, boxShadow, visibility, transform,
        // Editor-specific props that should NEVER be passed to DOM
        isSelected, isHighlighted, onSelect, onUpdateProps, onDelete, onDuplicate, onCloseSelection,
        droppableRef, isDroppableOver, layers,
        ...htmlAttributes
    } = props;

    return { style, htmlAttributes };
};

// ═══════════════════════════════════════════════════════════════
// Main Renderer
// ═══════════════════════════════════════════════════════════════

export interface PageRendererProps {
    layers: Layer[];
    products?: any[]; // For product grids
    resolvedData?: ResolvedData; // For dynamic data
    pageContext?: PageContextType; // New: Pass body/title
    siteId?: string; // Tenant ID for data isolation
    themeSettings?: PageContextType['themeSettings'];
}

export const PageRenderer: React.FC<PageRendererProps> = ({
    layers,
    products,
    resolvedData = { collections: {}, menus: {} },
    pageContext = {},
    siteId,
    themeSettings
}) => {
    // 1. Create a map of IDs to Layers for O(1) lookup
    const layerMap = React.useMemo(() => {
        const map = new Map<string, Layer>();
        if (!layers || !Array.isArray(layers)) return map;
        layers.forEach((layer) => {
            if (layer && layer.id) {
                map.set(layer.id, layer);
            }
        });
        return map;
    }, [layers]);

    // 2. Identify root layers (layers without parentId or parentId not in map)
    const rootLayers = React.useMemo(() => {
        if (!layers || !Array.isArray(layers)) return [];
        return layers.filter((layer) => {
            return !layer.parentId || !layerMap.has(layer.parentId);
        });
    }, [layers, layerMap]);

    // 3. Recursive rendering function
    const renderLayer = (layerId: string) => {
        const layer = layerMap.get(layerId);
        if (!layer) return null;

        // Render children recursively
        const renderedChildren = layer.children?.map((childId) => renderLayer(childId));

        // Get Component from Central Registry
        const Component = getComponentByType(layer.type);

        if (!Component) {
            console.warn(`[PageRenderer] Unknown component type: ${layer.type}`);
            return null;
        }

        // Extract styles and other props
        const { style, htmlAttributes } = extractStyles(layer.props || {});

        // Prepare Props
        const componentProps: Record<string, any> = {
            ...layer.props, // Pass raw props (text, variant, etc.)
            ...htmlAttributes, // Pass filtered HTML attributes
            style, // Pass extracted styles object (includes responsive styles)
            id: layer.id,
            className: cn(layer.props.className), // Ensure custom classNames are passed
            products: products, // Inject products if available
        };

        // 🧠 SMART INJECTION: Only pass if requested to avoid React DOM warnings
        if (layer.props.collectionId && resolvedData.collections[layer.props.collectionId]) {
            componentProps.collectionData = resolvedData.collections[layer.props.collectionId];
        }
        if (layer.props.menuHandle && resolvedData.menus[layer.props.menuHandle]) {
            componentProps.menuData = resolvedData.menus[layer.props.menuHandle];
        }

        // Render Component
        return (
            // @ts-ignore
            <Component key={layer.id} {...componentProps}>
                {renderedChildren}
            </Component>
        );
    };

    return (
        <SiteProvider siteId={siteId || (pageContext.siteId as string)}>
            <PageContext.Provider value={{ ...pageContext, themeSettings, siteId: siteId || pageContext.siteId }}>
                <div className="w-full h-full min-h-screen">
                    {rootLayers.map((rootLayer) => renderLayer(rootLayer.id))}
                </div>
            </PageContext.Provider>
        </SiteProvider>
    );
};
